/* eslint no-undef:0 no-use-before-define:0 */
import _isString from 'lodash/isString';
import _isEmpty from 'lodash/isEmpty';
import { pageNames } from './page-name-identifiers';
import Page from './page';
import poll from './poll';
import { isC2MerchantMigrated } from './globalHelper';
import { PRODUCT, SPACE } from '../../../constants/fptiConstants';

const maskRegEx =
  /(\d\d+|(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,}))/g; // eslint-disable-line
const maskPattern = '#########';

// FPTI object checker
export const trackingHelper = (callback, options) =>
  poll(Page.validateFpti, 4, 500)
    .then(() => {
      'inside callback';

      callback(options);
    })
    .catch(() => {});

const isC2MerchantCheck = (existingCountyLocale) => {
  // if user is c2merhcnat migrated change the country/locale else return existing country/locale
  if (isC2MerchantMigrated()) {
    const country = 'CN';
    const locale = Page.getLanguage() === 'en' ? 'en_US' : 'zh_CN';

    return `${country}|${locale}`;
  }

  return existingCountyLocale;
};

const shouldClearHpid = (pageId) => {
  if (pageId === 'ICFTopic') {
    return Page.isIcfTopicsPage();
  }
  // returning false for article, topic, home and complaints page so that hpid is not removed
  return pageId !== 'articlePage' && pageId !== 'complaintsPage' && pageId !== 'topicPage' && pageId !== 'homePage';
};

// Send FPTI click event with waiting for FPTI option available
export const trackLink = (params) =>
  trackingHelper((options) => {
    const fpti = pageFPTIFormate(options);

    const pageId = Page.getFPTIPageId();

    // Clearing the hpid in click events in all the pages except article and complaints page
    if (shouldClearHpid(pageId)) {
      fpti.hpid = '';
    }

    if (options.feedback) {
      fpti.hcar = options.feedback.hcar;
      fpti.hcaf = options.feedback.hcaf;
    } else {
      // Clear hcar and hcaf
      fpti.hcar = '';
      fpti.hcaf = '';
    }

    // clearing out error props in case they exist
    fpti.erpg = '';
    fpti.erfd = '';
    fpti.eccd = '';

    // setting component
    fpti.comp = 'smarthelpnodeweb';

    const existingCountyLocale = fpti.rsta || getRSTA();

    fpti.rsta = isC2MerchantCheck(existingCountyLocale);

    try {
      PAYPAL.analytics.Analytics.prototype.recordClick({ data: fpti });
    } catch (e) {
      // suppress error
    }
  }, params);

// Send FPTI impression event with waiting for FPTI option available
export const trackPage = (params) =>
  trackingHelper((options) => {
    const fpti = Object.assign((window && window.fpti) || {}, pageFPTIFormate(options));

    // clearing out error props in case they exist
    fpti.erpg = undefined;
    fpti.erfd = undefined;
    fpti.eccd = undefined;

    // setting component
    fpti.comp = 'smarthelpnodeweb';

    const existingCountyLocale = fpti.rsta || getRSTA();

    fpti.rsta = isC2MerchantCheck(existingCountyLocale);

    try {
      if (!_isEmpty(fpti.event_name) && !_isEmpty(fpti.e) && fpti.e === 'ac') {
        PAYPAL.analytics.Analytics.prototype.recordEvent(fpti.event_name, { data: fpti });
      } else {
        PAYPAL.analytics.Analytics.prototype.recordImpression({ data: fpti });
      }
      // resetting back custom events
      fpti.event_name = undefined;
      fpti.e = undefined;
    } catch (e) {
      // suppress error
    }
  }, params);

export const trackError = (params) =>
  trackingHelper((options) => {
    // const fpti = {};
    // set page name
    const pageDetails = getPageDetails(options.id);
    fpti.pgrp = pageDetails.pageGroupName;
    fpti.page = pageDetails.pageName;

    if (options.errorField) {
      fpti.erpg = pageDetails.pageName;
      fpti.erfd = options.errorField;
    }

    PAYPAL.analytics.Analytics.prototype.recordImpression({ data: fpti });
  }, params);

export const trackSetUp = (params) =>
  trackingHelper((options) => {
    if (typeof window.fptiData === 'undefined') {
      window.fptiData = '';
    }

    if (typeof window.fptiUrl === 'undefined') {
      window.fptiUrl = '';
    }

    let fptiTmp = fptiData || '';
    const customerOptions = pageFPTIFormate(options);

    Object.keys(customerOptions).forEach((key) => {
      fptiTmp = Page.URLReplacement(fptiTmp, key, customerOptions[key]);
    });

    // If user is c2merchantmigrated replacing the rsta
    if (isC2MerchantMigrated() && fptiTmp) {
      const locale = Page.getLanguage() === 'en' ? 'en_US' : 'zh_CN';

      try {
        // spliting the fpti data to find the index of rsta to change country/locale for c2merchantmigrated users
        const rstaIndex = fptiTmp.split('&') && fptiTmp.split('&').findIndex((fpti) => fpti.includes('rsta'));

        fptiTmp = fptiTmp.split('&');

        // Replacing the rsta value for c2merchantmigrated users
        fptiTmp[rstaIndex] = `rsta=${locale}`;

        // joining the fpti data after  Replacing the rsta value for c2merchantmigrated users
        fptiTmp = fptiTmp.join('&');
      } catch (e) {
        console.info('C2_MERCHANT_MIGRATED_RSTA_CHANGE_FAILED'); // eslint-disable-line no-console
      }
    }

    PAYPAL.core = PAYPAL.core || {};
    PAYPAL.core.pta = PAYPAL.analytics.setup({ data: fptiTmp, url: fptiUrl, trackFormAbandonment: false });
    PAYPAL.analytics.startClientErrorTracking();
  }, params);

function getPageDetails(id) {
  return pageNames[id];
}

export function dataMasking(data) {
  if (_isString(data)) {
    return data.replace(maskRegEx, maskPattern);
  }

  return data;
}

// Country and locale info in the FPTI
function getRSTA() {
  return `${Page.getLocale().replace('-', '_')}`;
}

function pageFPTIFormate(options) {
  const fptiKeys = {};
  fptiKeys.link = options.link; // set link name

  const pageDetails = getPageDetails(options.id); // set page name
  fptiKeys.pgrp = `${pageDetails.pageGroupName}`;
  fptiKeys.page = `${pageDetails.pageName}`;
  fptiKeys.event_name = pageDetails.event_name;
  fptiKeys.e = pageDetails.e;
  fptiKeys.lgin = Page.getFPTILogin();

  if (options.link) {
    fptiKeys.pglk = `${pageDetails.pageGroupName}|${options.link}`;
    fptiKeys.pgln = `${pageDetails.pageName}|${options.link}`;
  } else {
    fptiKeys.pglk = '';
    fptiKeys.pgln = '';
  }

  if (options.article_doc_id) {
    fptiKeys.article_doc_id = options.article_doc_id;
  } else {
    fptiKeys.article_doc_id = '';
  }

  // add hpid if it is part of the options, else clear it
  if (options.hpid) {
    fptiKeys.hpid = options.hpid;
  } else {
    fptiKeys.hpid = '';
  }

  // add help_component_ids if it is part of the options, else clear it
  if (options.help_component_ids) {
    fptiKeys.help_component_ids = options.help_component_ids;
  } else {
    fptiKeys.help_component_ids = '';
  }

  // add help_doc_id if it is part of the options, else clear it
  if (options.help_doc_id) {
    fptiKeys.help_doc_id = options.help_doc_id;
  } else {
    fptiKeys.help_doc_id = '';
  }

  if (options.srch) {
    fptiKeys.srch = dataMasking(options.srch);
  } else {
    fptiKeys.srch = '';
  }

  // For Omnichannel added new FPTI keys
  if (options.ocp_context_metadata) {
    fptiKeys.ocp_context_metadata = options.ocp_context_metadata;
  }

  if (options.timestamp) {
    fptiKeys.t = options.timestamp;
  }

  if (options.context_status) {
    fptiKeys.context_status = options.context_status;
  }

  // customer confirmed
  fptiKeys.link_name = options.link_name;

  if (options.channel_name) {
    fptiKeys.channel_name = options.channel_name;
  }

  // Set product and space tags for tying events to Help Center IQ dashboard
  fptiKeys.product = PRODUCT;
  fptiKeys.space_key = SPACE;

  return fptiKeys;
}
